import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { ConfidenceField, DocumentPreview } from "@/components/DocumentPreview";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  UploadCloud, FileText, CheckCircle2, XCircle, AlertTriangle, ArrowLeft, Loader2, Ban, DollarSign, Pencil, RefreshCw, GitBranch,
} from "lucide-react";

const ACCEPTED = ".pdf,.jpg,.jpeg,.png,.heic,.heif";

const VEREDICTO_META = {
  valida_para_pago: { label: "VALIDA PARA PAGO", cls: "border-green-300 bg-green-50 text-green-800", icon: CheckCircle2 },
  ya_pagada: { label: "YA PAGADA", cls: "border-red-300 bg-red-50 text-red-800", icon: AlertTriangle },
  posible_duplicado: { label: "POSIBLE DUPLICADO", cls: "border-red-300 bg-red-50 text-red-800", icon: AlertTriangle },
  monto_no_coincide: { label: "MONTO NO COINCIDE", cls: "border-amber-300 bg-amber-50 text-amber-800", icon: AlertTriangle },
  load_invalidado: { label: "LOAD INVALIDADO", cls: "border-red-300 bg-red-50 text-red-800", icon: AlertTriangle },
  no_registrada: { label: "NO REGISTRADA", cls: "border-red-300 bg-red-50 text-red-800", icon: XCircle },
  requiere_seleccion: { label: "REQUIERE SELECCION", cls: "border-blue-300 bg-blue-50 text-blue-800", icon: GitBranch },
};

function fieldVal(campos, key) { return campos?.[key]?.valor ?? null; }
function fieldConf(campos, key) { return campos?.[key]?.confianza || "baja"; }

export default function DocumentUploadInvoice() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [queue, setQueue] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [activeDocId, setActiveDocId] = useState(null);

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList);
    if (files.length === 0) return;
    for (const file of files) {
      const tempId = `${file.name}-${Date.now()}-${Math.random()}`;
      setQueue((q) => [...q, { tempId, filename: file.name, status: "subiendo", doc: null }]);
      try {
        const fd = new FormData();
        fd.append("file", file);
        setQueue((q) => q.map((it) => (it.tempId === tempId ? { ...it, status: "extrayendo" } : it)));
        const { data } = await api.post("/documents/upload-invoice", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setQueue((q) => q.map((it) => (it.tempId === tempId
          ? { ...it, status: data.estado_procesamiento === "error" ? "error" : "extraido", doc: data }
          : it)));
      } catch (e) {
        setQueue((q) => q.map((it) => (it.tempId === tempId
          ? { ...it, status: "error", errorMsg: formatApiErrorDetail(e.response?.data?.detail) }
          : it)));
      }
    }
    if (fileRef.current) fileRef.current.value = "";
  };

  const onDrop = (e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); };
  const activeItem = queue.find((q) => q.doc?.id === activeDocId);

  const refreshItem = (updatedDoc) => {
    setQueue((q) => q.map((it) => (it.doc?.id === updatedDoc.id ? { ...it, doc: updatedDoc, status: updatedDoc.estado_procesamiento } : it)));
  };

  const reprocesar = async (tempId, docId) => {
    setQueue((q) => q.map((it) => (it.tempId === tempId ? { ...it, status: "extrayendo" } : it)));
    try {
      const { data } = await api.post(`/documents/${docId}/reprocesar`);
      setQueue((q) => q.map((it) => (it.tempId === tempId
        ? { ...it, status: data.estado_procesamiento === "error" ? "error" : "extraido", doc: data, errorMsg: data.error_mensaje }
        : it)));
    } catch (e) {
      setQueue((q) => q.map((it) => (it.tempId === tempId
        ? { ...it, status: "error", errorMsg: formatApiErrorDetail(e.response?.data?.detail) }
        : it)));
    }
  };

  if (activeItem) {
    return <InvoiceVerdictPanel item={activeItem} onUpdated={refreshItem} onBack={() => setActiveDocId(null)} />;
  }

  return (
    <div>
      <PageHeader
        title="Validar factura de carrier"
        subtitle="Sube la factura y el sistema la cruzara contra los loads registrados para detectar pagos duplicados o inconsistencias"
        actions={
          <Button variant="outline" onClick={() => navigate("/documents")} className="gap-2" data-testid="docinvoice-back-to-documents">
            <ArrowLeft className="h-4 w-4" /> Volver a Documentos
          </Button>
        }
      />

      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => fileRef.current?.click()}
        data-testid="doc-invoice-dropzone"
        className={`mb-6 flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-10 text-center transition-colors ${
          dragOver ? "border-primary bg-green-50" : "border-border bg-white hover:bg-slate-50"
        }`}
      >
        <UploadCloud className="h-8 w-8 text-primary" />
        <p className="text-sm font-medium text-foreground">Arrastra y suelta facturas aqui, o haz clic para seleccionar</p>
        <p className="text-xs text-muted-foreground">PDF, JPG, PNG o HEIC - maximo 10 MB por archivo. Puedes subir varias a la vez.</p>
        <input ref={fileRef} type="file" multiple accept={ACCEPTED} className="hidden" onChange={(e) => handleFiles(e.target.files)} data-testid="doc-invoice-file-input" />
      </div>

      {queue.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="font-heading text-base">Facturas procesadas</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {queue.map((item, i) => {
              const meta = VEREDICTO_META[item.doc?.veredicto];
              return (
                <div key={item.tempId} className="flex items-center justify-between rounded-md border border-border p-3" data-testid={`doc-invoice-queue-item-${i}`}>
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">{item.filename}</p>
                      {(item.status === "subiendo" || item.status === "extrayendo") && (
                        <p className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Loader2 className="h-3 w-3 animate-spin" /> {item.status === "subiendo" ? "Subiendo..." : "Extrayendo con IA..."}
                        </p>
                      )}
                      {item.status === "error" && <p className="text-xs text-red-600">{item.errorMsg || "No se pudo procesar automaticamente"}</p>}
                      {meta && <Badge className={"mt-1 " + meta.cls}>{meta.label}</Badge>}
                    </div>
                  </div>
                  {(item.status === "extraido" || item.status === "confirmado" || item.status === "rechazado" || item.status === "error") && item.doc && (
                    <div className="flex gap-2">
                      {item.status === "error" && (
                        <Button size="sm" variant="outline" className="gap-1" onClick={() => reprocesar(item.tempId, item.doc.id)} data-testid={`doc-invoice-queue-reprocess-${i}`}>
                          <RefreshCw className="h-3.5 w-3.5" /> Reprocesar
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => setActiveDocId(item.doc.id)} data-testid={`doc-invoice-queue-view-${i}`}>Ver detalle</Button>
                    </div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}
    </div>
  );
}


function InvoiceVerdictPanel({ item, onUpdated, onBack }) {
  const doc = item.doc;
  const campos = doc.datos_extraidos || {};
  const meta = VEREDICTO_META[doc.veredicto] || { label: doc.veredicto || "SIN VEREDICTO", cls: "border-slate-300 bg-slate-50 text-slate-700", icon: AlertTriangle };
  const Icon = meta.icon;
  const detalle = doc.veredicto_detalle || {};

  const [payOpen, setPayOpen] = useState(false);
  const [payForm, setPayForm] = useState({ fecha_pago: "", referencia_pago: "" });
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectMotivo, setRejectMotivo] = useState("");
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustMonto, setAdjustMonto] = useState(detalle.monto_factura ?? "");
  const [adjustMoneda, setAdjustMoneda] = useState(detalle.moneda_factura || "USD");
  const [manualOpen, setManualOpen] = useState(false);
  const [manualMotivo, setManualMotivo] = useState("");
  const [busy, setBusy] = useState(false);
  const [selectingLoad, setSelectingLoad] = useState(false);

  const lowConfFields = ["nombre_carrier", "numero_factura", "monto_total"].filter(
    (k) => fieldVal(campos, k) == null || fieldConf(campos, k) === "baja");

  const runAction = async (payload) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/documents/${doc.id}/invoice-action`, payload);
      toast.success("Accion registrada");
      onUpdated(data);
      setPayOpen(false); setRejectOpen(false); setAdjustOpen(false); setManualOpen(false);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.code === "veredicto_cambio") {
        toast.warning(detail.message);
        setPayOpen(false);
        try {
          const { data: fresh } = await api.get(`/documents/${doc.id}`);
          onUpdated(fresh);
        } catch { /* ignore refresh failure, banner will stay stale until next reload */ }
      } else {
        toast.error(formatApiErrorDetail(detail));
      }
    } finally {
      setBusy(false);
    }
  };

  const selectLoad = async (loadId) => {
    setSelectingLoad(true);
    try {
      const { data } = await api.post(`/documents/${doc.id}/select-load`, { load_id: loadId });
      toast.success("Load seleccionado, veredicto recalculado");
      onUpdated(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSelectingLoad(false);
    }
  };

  const reprocesar = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/documents/${doc.id}/reprocesar`);
      toast.success("Documento reprocesado");
      onUpdated(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const isConfirmed = doc.estado_procesamiento === "confirmado" || doc.estado_procesamiento === "rechazado";

  return (
    <div>
      <PageHeader
        title="Validacion de factura"
        subtitle={`Documento: ${doc.archivo_nombre}`}
        actions={
          <Button variant="outline" onClick={onBack} className="gap-2" data-testid="docinvoice-back-to-queue">
            <ArrowLeft className="h-4 w-4" /> Volver
          </Button>
        }
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="h-[70vh] lg:h-[80vh]">
          <DocumentPreview docId={doc.id} mimeType={doc.archivo_tipo} testId="docinvoice-preview" />
        </div>

        <div className="space-y-4">
          <div className={`flex items-center gap-3 rounded-md border-2 p-4 ${meta.cls}`} data-testid="docinvoice-veredicto-banner">
            <Icon className="h-6 w-6 shrink-0" />
            <div>
              <p className="font-heading text-base font-bold" data-testid="docinvoice-veredicto-label">{meta.label}</p>
              <p className="text-sm">{doc.veredicto_mensaje}</p>
            </div>
          </div>

          <Card>
            <CardHeader><CardTitle className="font-heading text-base">Datos extraidos</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-2 gap-3 text-sm">
              <div className="col-span-2">
                <ConfidenceField confianza={fieldConf(campos, "nombre_carrier")} value={fieldVal(campos, "nombre_carrier")} testId="docinvoice-conf-carrier">
                  <p className="text-xs text-muted-foreground">Carrier</p><p className="font-medium">{fieldVal(campos, "nombre_carrier") || "-"}</p>
                </ConfidenceField>
                {doc.carrier_nombre && doc.carrier_match_score != null && (
                  <p className="mt-1 text-xs text-primary" data-testid="docinvoice-carrier-match-score">
                    Coincidencia en catalogo: {doc.carrier_nombre} ({Math.round(doc.carrier_match_score * 100)}%)
                  </p>
                )}
              </div>
              <ConfidenceField confianza={fieldConf(campos, "numero_factura")} value={fieldVal(campos, "numero_factura")} testId="docinvoice-conf-numero-factura">
                <p className="text-xs text-muted-foreground">N. factura</p><p className="font-mono">{fieldVal(campos, "numero_factura") || "-"}</p>
              </ConfidenceField>
              <ConfidenceField confianza={fieldConf(campos, "fecha_factura")} value={fieldVal(campos, "fecha_factura")} testId="docinvoice-conf-fecha-factura">
                <p className="text-xs text-muted-foreground">Fecha factura</p><p>{fieldVal(campos, "fecha_factura") || "-"}</p>
              </ConfidenceField>
              <ConfidenceField confianza={fieldConf(campos, "monto_total")} value={fieldVal(campos, "monto_total")} testId="docinvoice-conf-monto">
                <p className="text-xs text-muted-foreground">Monto</p><p className="font-mono">{fieldVal(campos, "monto_total") ?? "-"} {fieldVal(campos, "moneda") || ""}</p>
              </ConfidenceField>
              <div className="col-span-2"><p className="text-xs text-muted-foreground">Loads referenciados</p><p className="font-mono">{(fieldVal(campos, "numeros_de_load_referenciados") || []).join(", ") || "-"}</p></div>
              <div className="col-span-2"><p className="text-xs text-muted-foreground">Conceptos</p><p>{fieldVal(campos, "conceptos") || "-"}</p></div>
            </CardContent>
          </Card>

          {lowConfFields.length > 0 && (
            <div className="rounded-md border border-red-300 bg-red-50 p-3" data-testid="docinvoice-low-confidence-warning">
              <div className="flex items-center gap-2 font-medium text-red-800"><AlertTriangle className="h-4 w-4" /> Datos clave con confianza baja o vacios</div>
              <p className="mt-1 text-sm text-red-800">
                Revisa el documento original antes de tomar cualquier accion: {lowConfFields.join(", ")}.
              </p>
            </div>
          )}

          {doc.veredicto === "ya_pagada" && (
            <Card data-testid="docinvoice-ya-pagada-detalle">
              <CardHeader><CardTitle className="font-heading text-base">Pago anterior registrado</CardTitle></CardHeader>
              <CardContent className="text-sm space-y-1">
                <p>Load: <span className="font-mono">{detalle.numero_load}</span></p>
                <p>Fecha de pago: {detalle.fecha_pago}</p>
                <p>Referencia: {detalle.referencia_pago}</p>
                <p>Registrado por: {detalle.registrado_por || "-"}</p>
              </CardContent>
            </Card>
          )}
          {doc.veredicto === "posible_duplicado" && (
            <Card data-testid="docinvoice-duplicado-detalle">
              <CardHeader><CardTitle className="font-heading text-base">Factura anterior similar</CardTitle></CardHeader>
              <CardContent className="text-sm space-y-1">
                <p>Procesada el: {detalle.fecha_proceso_anterior?.slice(0, 10)}</p>
                <p>Veredicto anterior: {detalle.veredicto_anterior}</p>
                <p>Accion tomada: {detalle.accion_tomada_anterior || "ninguna aun"}</p>
              </CardContent>
            </Card>
          )}
          {doc.veredicto === "monto_no_coincide" && (
            <Card data-testid="docinvoice-monto-detalle">
              <CardHeader><CardTitle className="font-heading text-base">Diferencia de monto</CardTitle></CardHeader>
              <CardContent className="text-sm space-y-1">
                <p>Load: <span className="font-mono">{detalle.numero_load}</span></p>
                <p>Monto factura: {detalle.monto_factura} {detalle.moneda_factura}</p>
                <p>Monto cargado en el load: {detalle.monto_costo} {detalle.moneda_costo}</p>
                <p className="font-semibold">Diferencia: {detalle.diferencia_usd} USD ({detalle.diferencia_pct}%)</p>
              </CardContent>
            </Card>
          )}
          {doc.veredicto === "load_invalidado" && (
            <Card data-testid="docinvoice-invalidado-detalle">
              <CardContent className="pt-4 text-sm">
                <p>Load {detalle.numero_load} fue invalidado. Reemplazado por: {detalle.reemplazado_por || "sin reemplazo"}.</p>
              </CardContent>
            </Card>
          )}
          {doc.veredicto === "requiere_seleccion" && (
            <Card data-testid="docinvoice-requiere-seleccion-detalle">
              <CardHeader><CardTitle className="font-heading text-base">Elegi a que load corresponde esta factura</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {(detalle.candidatos || []).map((c, i) => (
                  <div key={c.load_id} className="flex items-center justify-between rounded-md border border-blue-200 p-3" data-testid={`docinvoice-candidato-${i}`}>
                    <div className="text-sm">
                      <p className="font-mono font-semibold">{c.numero_load}</p>
                      <p className="text-xs text-muted-foreground">Costo cargado: {c.monto} {c.moneda} - {c.estado_pago}</p>
                    </div>
                    <Button size="sm" disabled={selectingLoad} onClick={() => selectLoad(c.load_id)} data-testid={`docinvoice-select-candidato-${i}`}>
                      Elegir este load
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
          {detalle.viaje_completo && (
            <Card data-testid="docinvoice-viaje-completo">
              <CardHeader><CardTitle className="font-heading text-base">Viaje transfronterizo completo</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-2 gap-3 text-sm">
                {["tramo_actual", "tramo_hermano"].map((key) => {
                  const leg = detalle.viaje_completo[key];
                  if (!leg) return null;
                  return (
                    <div key={key} className="rounded-md border border-border p-2">
                      <p className="text-xs text-muted-foreground">{key === "tramo_actual" ? "Este tramo" : "Tramo hermano"}</p>
                      <p className="font-mono font-semibold">{leg.numero_load}</p>
                      <p className="text-xs">Carrier: {leg.carrier_nombre || "-"}</p>
                      {(leg.costos_carrier || []).map((c, i) => (
                        <p key={i} className="text-xs">Costo: {c.monto} {c.moneda} ({c.estado_pago})</p>
                      ))}
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}

          {doc.accion_tomada && (
            <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800" data-testid="docinvoice-accion-tomada">
              Accion tomada: <span className="font-semibold">{doc.accion_tomada}</span> por {doc.accion_tomada_por} el {doc.accion_tomada_en?.slice(0, 10)}
            </div>
          )}

          {doc.estado_procesamiento === "error" && (
            <div className="rounded-md border border-red-300 bg-red-50 p-3" data-testid="docinvoice-error-banner">
              <div className="flex items-center gap-2 font-medium text-red-800"><XCircle className="h-4 w-4" /> No se pudo procesar automaticamente</div>
              <p className="mt-1 text-sm text-red-800">{doc.error_mensaje}</p>
              <Button size="sm" variant="outline" className="mt-2 gap-1" disabled={busy} onClick={reprocesar} data-testid="docinvoice-reprocesar-btn">
                <RefreshCw className="h-3.5 w-3.5" /> Reprocesar
              </Button>
            </div>
          )}

          {!isConfirmed && (
            <div className="flex flex-wrap gap-2 pb-6">
              {doc.veredicto === "valida_para_pago" && (
                <Button onClick={() => setPayOpen(true)} className="gap-2" data-testid="docinvoice-action-registrar-pago">
                  <DollarSign className="h-4 w-4" /> Registrar pago
                </Button>
              )}
              {doc.veredicto === "monto_no_coincide" && (
                <Button onClick={() => setAdjustOpen(true)} variant="outline" className="gap-2" data-testid="docinvoice-action-ajustar-costo">
                  <Pencil className="h-4 w-4" /> Ajustar costo del load
                </Button>
              )}
              {["ya_pagada", "posible_duplicado", "load_invalidado", "no_registrada", "monto_no_coincide"].includes(doc.veredicto) && (
                <Button onClick={() => setRejectOpen(true)} variant="destructive" className="gap-2" data-testid="docinvoice-action-rechazar">
                  <Ban className="h-4 w-4" /> Rechazar y archivar
                </Button>
              )}
              <Button onClick={() => setManualOpen(true)} variant="ghost" className="gap-2" data-testid="docinvoice-action-manual">
                Resolucion manual
              </Button>
            </div>
          )}
        </div>
      </div>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Registrar pago</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-muted-foreground">Fecha de pago *</Label>
              <Input type="date" className="mt-1" value={payForm.fecha_pago} onChange={(e) => setPayForm({ ...payForm, fecha_pago: e.target.value })} data-testid="docinvoice-pay-fecha" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Referencia de pago *</Label>
              <Input className="mt-1" value={payForm.referencia_pago} onChange={(e) => setPayForm({ ...payForm, referencia_pago: e.target.value })} data-testid="docinvoice-pay-referencia" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)}>Cancelar</Button>
            <Button disabled={busy || !payForm.fecha_pago || !payForm.referencia_pago}
              onClick={() => runAction({ accion: "registrar_pago", fecha_pago: payForm.fecha_pago, referencia_pago: payForm.referencia_pago })}
              data-testid="docinvoice-pay-confirm">
              {busy ? "Guardando..." : "Confirmar pago"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Rechazar y archivar factura</DialogTitle></DialogHeader>
          <div>
            <Label className="text-xs text-muted-foreground">Motivo *</Label>
            <Textarea className="mt-1" value={rejectMotivo} onChange={(e) => setRejectMotivo(e.target.value)} data-testid="docinvoice-reject-motivo" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>Cancelar</Button>
            <Button variant="destructive" disabled={busy || rejectMotivo.trim().length < 5}
              onClick={() => runAction({ accion: "rechazar", motivo: rejectMotivo })} data-testid="docinvoice-reject-confirm">
              {busy ? "Guardando..." : "Rechazar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={adjustOpen} onOpenChange={setAdjustOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Ajustar costo del load</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="rounded-md border border-border p-2">
              <p className="text-xs text-muted-foreground">Costo actual (en el load)</p>
              <p className="font-mono">{detalle.monto_costo} {detalle.moneda_costo}</p>
              {detalle.monto_costo_usd != null && <p className="text-xs text-muted-foreground">≈ {detalle.monto_costo_usd} USD</p>}
            </div>
            <div className="rounded-md border border-primary/40 bg-green-50 p-2">
              <p className="text-xs text-muted-foreground">Monto de la factura</p>
              <p className="font-mono">{detalle.monto_factura} {detalle.moneda_factura}</p>
              {detalle.monto_factura_usd != null && <p className="text-xs text-muted-foreground">≈ {detalle.monto_factura_usd} USD</p>}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-muted-foreground">Nuevo monto *</Label>
              <Input type="number" className="mt-1" value={adjustMonto} onChange={(e) => setAdjustMonto(e.target.value)} data-testid="docinvoice-adjust-monto" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Moneda *</Label>
              <Select value={adjustMoneda} onValueChange={setAdjustMoneda}>
                <SelectTrigger className="mt-1" data-testid="docinvoice-adjust-moneda"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="USD">USD</SelectItem><SelectItem value="MXN">MXN</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">El costo del load quedara en {adjustMonto || "0"} {adjustMoneda}, reemplazando el actual.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjustOpen(false)}>Cancelar</Button>
            <Button disabled={busy || !adjustMonto || !adjustMoneda}
              onClick={() => runAction({ accion: "ajustar_costo", nuevo_monto: parseFloat(adjustMonto), nueva_moneda: adjustMoneda })}
              data-testid="docinvoice-adjust-confirm">
              {busy ? "Guardando..." : "Ajustar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Marcar para resolucion manual</DialogTitle></DialogHeader>
          <div>
            <Label className="text-xs text-muted-foreground">Notas (opcional)</Label>
            <Textarea className="mt-1" value={manualMotivo} onChange={(e) => setManualMotivo(e.target.value)} data-testid="docinvoice-manual-motivo" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualOpen(false)}>Cancelar</Button>
            <Button disabled={busy} onClick={() => runAction({ accion: "manual", motivo: manualMotivo })} data-testid="docinvoice-manual-confirm">
              {busy ? "Guardando..." : "Confirmar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
